// src/pages/staff/OASettingsPage.jsx
import React, { useState, useEffect } from "react";
import Sidebar from "../../components/MasterSideBar.jsx";
import TopBar from "../../components/TopbarNav";
import { updateStaff, getOneStaff } from "../../api/staff-calls-api";
import Select from "react-select";
import countries from "../../utils/countryList";
import axios from "axios";

const countryOptions = countries.map((c) => ({ label: `${c.flag} ${c.name}`, value: c.name }));

const Section = ({ title, children }) => {
    const [isOpen, setIsOpen] = useState(true);
    return (
        <div className="border rounded mb-4">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full text-left px-4 py-2 bg-gray-100 font-semibold text-gray-700 flex justify-between items-center"
            >
                {title} <span>{isOpen ? "−" : "+"}</span>
            </button>
            {isOpen && <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>}
        </div>
    );
};

export default function OASettingsPage() {
    const user = JSON.parse(localStorage.getItem("user"));
    const staffId = user?.id;
    const token = localStorage.getItem("token"); // ✅ token for resignation

    const handleLogout = () => {
        localStorage.removeItem("token");
        window.location.href = "/";
    };
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [activeTab, setActiveTab] = useState("profile");

    const formatDate = (dateString) => (dateString ? new Date(dateString).toISOString().split("T")[0] : "");

    // Fetch staff data asynchronously
    useEffect(() => {
        async function fetchStaff() {
            try {
                const res = await getOneStaff(staffId);
                const data = res.data || res;

                const flattenedData = {
                    ...data,
                    dateOfBirth: formatDate(data.dateOfBirth),
                    villageOrBlock: data.residence?.villageOrBlock || "",
                    payam: data.residence?.payam || "",
                    county: data.residence?.county || "",
                    state: data.residence?.state || "",
                    jobTitle: data.jobTitle || "",
                    department: data.department || "",
                    dateOfEmployment: formatDate(data.dateOfEmployment),
                    employmentType: data.employmentType || "",
                    contractDuration: data.contractDuration || "",
                    salaryRange: data.salaryRange || "",
                    bankName: data.bankingDetails?.[0]?.bankName || "",
                    bankBranch: data.bankingDetails?.[0]?.bankBranch || "",
                    accountName: data.bankingDetails?.[0]?.accountName || "",
                    accountNumber: data.bankingDetails?.[0]?.accountNumber || "",
                    currency: data.bankingDetails?.[0]?.currency || "",
                    swiftCode: data.bankingDetails?.[0]?.swiftCode || "",
                    institution: data.qualifications?.[0]?.institution || "",
                    award: data.qualifications?.[0]?.award || "",
                    yearFrom: data.qualifications?.[0]?.yearFrom || "",
                    yearTo: data.qualifications?.[0]?.yearTo || "",
                    keyExpertise: data.workHistory?.keyExpertise || "",
                    recentEmployer: data.workHistory?.recentEmployer || "",
                    workrole: data.workHistory?.workrole || "",
                    periodOfService: data.workHistory?.periodOfService || "",
                    recentGrossAnnualPaymentRange: data.workHistory?.recentGrossAnnualPaymentRange || "",
                    nokName: data.nextOfKin?.name || "",
                    nokSex: data.nextOfKin?.sex || "",
                    nokNationality: data.nextOfKin?.nationality || "",
                    nokRelationship: data.nextOfKin?.relationship || "",
                    nokVillageOrBlock: data.nextOfKin?.residence?.villageOrBlock || "",
                    nokPayam: data.nextOfKin?.residence?.payam || "",
                    nokCounty: data.nextOfKin?.residence?.county || "",
                    nokState: data.nextOfKin?.residence?.state || "",
                    nokTel: data.nextOfKin?.tel || "",
                    nokMobile: data.nextOfKin?.mobile || "",
                    nokEmail: data.nextOfKin?.email || "",
                };
                setFormData(flattenedData);
            } catch (err) {
                setMessage({ type: "error", text: "Failed to load profile" });
            } finally {
                setLoading(false);
            }
        }
        fetchStaff();
    }, [staffId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);

        // Only include editable fields to reduce payload size
        const updatedStaff = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            gender: formData.gender,
            dateOfBirth: formData.dateOfBirth,
            maritalStatus: formData.maritalStatus,
            nationality: formData.nationality,
            nationalIdOrPassport: formData.nationalIdOrPassport,
            tel: formData.tel,
            mobile: formData.mobile,
            role: formData.role,

            residence: {
                villageOrBlock: formData.villageOrBlock,
                payam: formData.payam,
                county: formData.county,
                state: formData.state,
            },

            jobTitle: formData.jobTitle,
            department: formData.department,
            dateOfEmployment: formData.dateOfEmployment,
            employmentType: formData.employmentType,
            contractDuration: formData.contractDuration,
            salaryRange: formData.salaryRange,

            bankingDetails: [{
                bankName: formData.bankName,
                bankBranch: formData.bankBranch,
                accountName: formData.accountName,
                accountNumber: formData.accountNumber,
                currency: formData.currency,
                swiftCode: formData.swiftCode,
            }],

            qualifications: [{
                institution: formData.institution,
                award: formData.award,
                yearFrom: formData.yearFrom,
                yearTo: formData.yearTo,
            }],

            workHistory: {
                keyExpertise: formData.keyExpertise,
                recentEmployer: formData.recentEmployer,
                workrole: formData.workrole,
                periodOfService: formData.periodOfService,
                recentGrossAnnualPaymentRange: formData.recentGrossAnnualPaymentRange,
            },

            nextOfKin: {
                name: formData.nokName,
                sex: formData.nokSex,
                nationality: formData.nokNationality,
                relationship: formData.nokRelationship,
                residence: {
                    villageOrBlock: formData.nokVillageOrBlock,
                    payam: formData.nokPayam,
                    county: formData.nokCounty,
                    state: formData.nokState,
                },
                tel: formData.nokTel,
                mobile: formData.nokMobile,
                email: formData.nokEmail,
            },
        };

        try {
            await updateStaff(staffId, updatedStaff);
            setMessage({ type: "success", text: "Profile updated successfully" });
        } catch (err) {
            setMessage({ type: "error", text: "Failed to update profile" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex min-h-screen bg-gray-50 font-sans">
            {/* Sidebar always rendered immediately */}
            <Sidebar visible={sidebarOpen} onClose={() => setSidebarOpen(false)} onLogout={handleLogout} />

            <main className="flex-1 p-6 overflow-y-auto md:ml-64">
                {/* Topbar always rendered immediately */}
                <TopBar onSidebarToggle={() => setSidebarOpen(true)} />

                {/* ✅ Settings header with Resignation button */}
                <div className="flex items-center justify-between mb-6 mt-20">
                    <h1 className="text-2xl font-bold text-gray-700">My Settings</h1>
                </div>

                {/* Message */}
                {message && (
                    <div className={`p-3 mb-4 rounded ${message.type === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {message.text}
                    </div>
                )}

                {/* Form container */}
                <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow p-4 sm:p-6 space-y-6 max-w-full md:max-w-5xl mx-auto">

                    {/* Show loading overlay on form only */}
                    {loading && (
                        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-xl">
                            <div className="text-gray-500 text-lg">Loading profile...</div>
                        </div>
                    )}

                    {/* Personal Information */}
                    <Section title="Personal Information">
                        {renderField("First Name", "firstName", "text", null, true)}
                        {renderField("Last Name", "lastName", "text", null, true)}
                        {renderField("Email", "email", "email", null, true)}
                        {renderField("Gender", "gender", "text", ["Male", "Female"], true)}
                        {renderField("Date of Birth", "dateOfBirth", "date", null, true)}
                        {renderField("Role", "role", "text", null, true)}
                        {renderField("Marital Status", "maritalStatus", "text", ["Single", "Married", "Other"])}
                        <div>
                            <label>Nationality</label>
                            <Select
                                options={countryOptions}
                                value={countryOptions.find((opt) => opt.value === formData.nationality) || null}
                                onChange={(sel) => setFormData({ ...formData, nationality: sel?.value || "" })}
                                className="react-select-container mt-1"
                                classNamePrefix="react-select"
                                isClearable
                            />
                        </div>
                        {renderField("National ID or Passport", "nationalIdOrPassport")}
                        {renderField("Village or Block", "villageOrBlock")}
                        {renderField("Payam", "payam")}
                        {renderField("County", "county")}
                        {renderField("State", "state")}
                        {renderField("Telephone", "tel")}
                        {renderField("Mobile", "mobile")}
                    </Section>

                    {/* Employment Details */}
                    <Section title="Employment Details">
                        {renderField("Job Title", "jobTitle", "text", null, true)}
                        {renderField("Department", "department", "text", null, true)}
                        {renderField("Date of Employment", "dateOfEmployment", "date", null, true)}
                        {renderField("Employment Type", "employmentType", "text", null, true)}
                        {renderField("Contract Duration", "contractDuration", "text", null, true)}
                        {renderField("Salary Range", "salaryRange", "text", null, true)}
                    </Section>

                    {/* Banking Details */}
                    <Section title="Banking Details">
                        {renderField("Bank Name", "bankName")}
                        {renderField("Bank Branch", "bankBranch")}
                        {renderField("Account Name", "accountName")}
                        {renderField("Account Number", "accountNumber")}
                        {renderField("Currency", "currency")}
                        {renderField("SWIFT Code", "swiftCode")}
                    </Section>

                    {/* Education & Work History */}
                    <Section title="Education & Work History">
                        {renderField("Institution", "institution")}
                        {renderField("Award", "award")}
                        {renderField("Year From", "yearFrom")}
                        {renderField("Year To", "yearTo")}
                        {renderField("Key Expertise", "keyExpertise")}
                        {renderField("Recent Employer", "recentEmployer")}
                        {renderField("Role", "workrole")}
                        {renderField("Period of Service", "periodOfService")}
                        {renderField("Recent Gross Annual Payment Range", "recentGrossAnnualPaymentRange")}
                    </Section>

                    {/* Next of Kin */}
                    <Section title="Next of Kin Information">
                        {renderField("Next of Kin Name", "nokName")}
                        <div>
                            <label>Next of Kin Nationality</label>
                            <Select
                                options={countryOptions}
                                value={countryOptions.find((opt) => opt.value === formData.nokNationality) || null}
                                onChange={(sel) => setFormData({ ...formData, nokNationality: sel?.value || "" })}
                                className="react-select-container mt-1"
                                classNamePrefix="react-select"
                                isClearable
                            />
                        </div>
                        {renderField("Next of Kin Relationship", "nokRelationship")}
                        {renderField("Next of Kin Village or Block", "nokVillageOrBlock")}
                        {renderField("Next of Kin Payam", "nokPayam")}
                        {renderField("Next of Kin County", "nokCounty")}
                        {renderField("Next of Kin State", "nokState")}
                        {renderField("Next of Kin Telephone", "nokTel")}
                        {renderField("Next of Kin Mobile", "nokMobile")}
                        {renderField("Next of Kin Email", "nokEmail", "email")}
                    </Section>

                    <div className="flex flex-col sm:flex-row justify-end gap-4">
                        <button type="submit" disabled={saving} className="px-4 py-2 rounded bg-orange-600 text-white hover:bg-orange-700">
                            {saving ? "Saving..." : "Save Changes"}
                        </button>
                    </div>
                </form>
            </main>
        </div>
    );

    function renderField(label, name, type = "text", options, readOnly = false) {
        return (
            <label className="block">
                {label}
                {options ? (
                    <select
                        name={name}
                        value={formData[name] || ""}
                        onChange={handleChange}
                        className={`w-full border rounded px-3 py-2 mt-1 ${readOnly ? "bg-gray-100 cursor-not-allowed" : ""}`}
                        disabled={readOnly}
                    >
                        <option value="">-- Select {label} --</option>
                        {options.map((opt, i) => (
                            <option key={i} value={opt}>
                                {opt}
                            </option>
                        ))}
                    </select>
                ) : (
                    <input
                        name={name}
                        type={type}
                        value={formData[name] || ""}
                        onChange={handleChange}
                        className={`w-full border rounded px-3 py-2 mt-1 ${readOnly ? "bg-gray-100" : "bg-white"}`}
                        readOnly={readOnly}
                    />
                )}
            </label>
        );
    }
}
